import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import axios from 'axios';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { useDirectVersionPublication } from '@/hooks/api/automations/useAutomationPublications.ts';
import type { EditorPublicationSummary } from '@/types/automationPublications.ts';
import type { AutomationVersionSimple } from '@/types/automations.ts';
import VersionComparisonModal from '@/pages/Editor/components/VersionHistory/VersionComparison/VersionComparisonModal.tsx';
import {
  publicationTestStatusAcknowledgement,
  publicationTestStatusSummary,
} from './publicationTestStatus.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  summary: EditorPublicationSummary;
  onOpenGuided: () => void;
  onOpenAdvanced: () => void;
  latestVersion: AutomationVersionSimple;
  publishedVersion: AutomationVersionSimple | null;
};

export default function SimplePublishReviewModal({
  opened,
  onClose,
  summary,
  onOpenGuided,
  onOpenAdvanced,
  latestVersion,
  publishedVersion,
}: Props) {
  const [error, setError] = useState('');
  const [comparisonOpened, setComparisonOpened] = useState(false);
  const [testStatusConfirmed, setTestStatusConfirmed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const requestId = useRef<string | null>(null);
  const session = useRef(0);
  const directPublication = useDirectVersionPublication();
  const draft = summary.draft_version;
  const canPublish = summary.next_action === 'publish_direct_version';
  const needsGuidedSelection = summary.journey === 'guided_dependency_selection';
  const includedAutomationCount = summary.direct_graph?.members ?? 1;
  const effectiveTestStatus =
    includedAutomationCount > 1 ? summary.direct_graph?.test_status : summary.test_status;
  const needsTestStatusConfirmation =
    !effectiveTestStatus || effectiveTestStatus.overall_status !== 'passing';
  const testStatusTitle =
    includedAutomationCount > 1
      ? `Tests across ${includedAutomationCount} included automations`
      : 'Version test status';
  const testStatusKey = effectiveTestStatus
    ? [
        effectiveTestStatus.overall_status,
        effectiveTestStatus.total_configured_tests,
        effectiveTestStatus.passed,
        effectiveTestStatus.failed,
        effectiveTestStatus.not_run,
        effectiveTestStatus.running,
        effectiveTestStatus.incomplete,
        effectiveTestStatus.repair_needed,
      ].join(':')
    : 'unavailable';
  const close = () => {
    if (!publishing) onClose();
  };

  useEffect(() => {
    session.current += 1;
    if (!opened) return;
    setError('');
    setPublishing(false);
    requestId.current = null;
  }, [opened, summary.automation_id, summary.draft_version?.id]);

  useEffect(() => {
    if (opened) setTestStatusConfirmed(false);
  }, [opened, testStatusKey]);

  const publish = async () => {
    if (!canPublish || publishing || (needsTestStatusConfirmation && !testStatusConfirmed)) return;
    const request = requestId.current ?? crypto.randomUUID();
    const publishSession = session.current;
    requestId.current = request;
    setPublishing(true);
    setError('');
    try {
      await directPublication.mutateAsync({
        automationId: summary.automation_id,
        request_id: request,
        expected_draft_version_id: draft?.id ?? '',
        expected_head_revision: summary.head_revision,
        acknowledged_warnings: needsTestStatusConfirmation ? ['version_test_status_attention'] : [],
      });
      if (publishSession !== session.current) return;
      onClose();
    } catch (cause) {
      if (publishSession !== session.current) return;
      if (
        axios.isAxiosError(cause) &&
        cause.response &&
        cause.response.status >= 400 &&
        cause.response.status < 500
      )
        requestId.current = null;
      setError('This version could not be published. Refresh the review and try again.');
    } finally {
      if (publishSession === session.current) setPublishing(false);
    }
  };

  return (
    <StyledModal
      opened={opened}
      onClose={close}
      title={`Publish v${draft?.number ?? ''}`}
      size="md"
    >
      <Stack>
        <Group gap="xs" wrap="wrap">
          <Text>
            Publish v{draft?.number}{' '}
            {summary.published_version
              ? `over active v${summary.published_version.number}`
              : 'for the first time'}
            .
          </Text>
          {publishedVersion && (
            <Button
              color="gray"
              px={0}
              size="xs"
              variant="transparent"
              onClick={() => setComparisonOpened(true)}
            >
              View changes
            </Button>
          )}
        </Group>
        {summary.state === 'ready_to_publish_replacement' && (
          <Alert color="yellow" title="Replace an invalid active publication">
            This newer version will become active. The invalid historical publication remains in
            history and cannot be restored.
          </Alert>
        )}
        {canPublish ? (
          <>
            <Alert
              color={effectiveTestStatus?.overall_status === 'passing' ? 'green' : 'yellow'}
              title={testStatusTitle}
            >
              {publicationTestStatusSummary(effectiveTestStatus)}
            </Alert>
            {needsTestStatusConfirmation && (
              <Checkbox
                checked={testStatusConfirmed}
                disabled={publishing}
                onChange={(event) => setTestStatusConfirmed(event.currentTarget.checked)}
                label={publicationTestStatusAcknowledgement(effectiveTestStatus)}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" disabled={publishing} onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={needsTestStatusConfirmation && !testStatusConfirmed}
                loading={publishing}
                onClick={() => void publish()}
              >
                Publish v{draft?.number}
              </Button>
            </Group>
          </>
        ) : needsGuidedSelection ? (
          <Alert color="orange" title="Choose child implementations">
            <Text size="sm">
              This automation uses one or more logical child automations. Choose the exact child
              versions for this release before reviewing and publishing it.
            </Text>
            <Group mt="sm">
              <Button onClick={onOpenGuided}>Choose implementations</Button>
              <Button variant="default" onClick={onOpenAdvanced}>
                Use advanced release settings
              </Button>
            </Group>
          </Alert>
        ) : (
          <Alert color="orange" title="This version is not ready to publish">
            <Text size="sm">
              {summary.reasons[0]?.message ??
                'Resolve the listed issue before publishing this version.'}
            </Text>
            {summary.requires_advanced_release_configuration && (
              <Button mt="sm" variant="default" onClick={onOpenAdvanced}>
                Open advanced release settings
              </Button>
            )}
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}
      </Stack>
      <VersionComparisonModal
        opened={comparisonOpened}
        onClose={() => setComparisonOpened(false)}
        publishedVersion={publishedVersion}
        latestVersion={latestVersion}
        initialBaseVersionId={publishedVersion?.id}
        initialTargetVersionId={latestVersion.id}
      />
    </StyledModal>
  );
}
